// Component for checking room availability
import { useState } from 'react';
import { parseExamDate, isSameDay } from '../utils/dateUtils';

const RoomAvailability = ({ examSchedule, building, room, onResultUpdate, selectedDate }) => {
  const [isSearching, setIsSearching] = useState(false);
  
  // Helper function to parse exam time and check if it's ongoing
  const isExamOngoing = (exam) => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Parse the exam time range (e.g., "8:00 AM - 11:00 AM")
    const [startTimeStr, endTimeStr] = exam.time.split(' - ');
    
    // Parse start time
    const [startTime, startPeriod] = startTimeStr.split(' ');
    const [startHourStr, startMinuteStr] = startTime.split(':');
    let startHour = parseInt(startHourStr);
    const startMinute = parseInt(startMinuteStr);
    
    // Convert start time to 24-hour format
    if (startPeriod === 'PM' && startHour < 12) startHour += 12;
    if (startPeriod === 'AM' && startHour === 12) startHour = 0;
    
    // Parse end time
    const [endTime, endPeriod] = endTimeStr.split(' ');
    const [endHourStr, endMinuteStr] = endTime.split(':');
    let endHour = parseInt(endHourStr);
    const endMinute = parseInt(endMinuteStr);
    
    // Convert end time to 24-hour format
    if (endPeriod === 'PM' && endHour < 12) endHour += 12;
    if (endPeriod === 'AM' && endHour === 12) endHour = 0;
    
    // Convert times to minutes for easier comparison
    const currentTimeInMinutes = currentHour * 60 + currentMinutes;
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;
    
    // Exam is ongoing if current time is between start and end time
    return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes <= endTimeInMinutes;
  };
  
  const checkAvailability = () => {
    if (!building || !room) return;
    
    setIsSearching(true);
    
    setTimeout(() => {
      // Get today's date as a Date object for reliable comparison
      const now = new Date();
      
      // Find ALL exams in this building (to check if it exists)
      const buildingExists = examSchedule.some(
        exam => exam.building === building
      );
      
      // Find ALL rooms in this building
      const roomsInBuilding = [...new Set(
        examSchedule
          .filter(exam => exam.building === building)
          .map(exam => exam.room)
      )];
      
      // Check if this building/room combination exists in the database at all
      const roomExists = examSchedule.some(
        exam => exam.building === building && exam.room === room
      );
      
      // If building doesn't exist
      if (!buildingExists) {
        onResultUpdate({
          available: false,
          examDetails: null,
          upcomingToday: null,
          isExamToday: false,
          invalidRoom: true,
          invalidBuilding: true,
          roomsInBuilding: [],
          checkDate: selectedDate || 'today'
        });
        setIsSearching(false);
        return;
      }
      
      // If room doesn't exist in our database, return invalid result
      if (!roomExists) {
        onResultUpdate({
          available: false,
          examDetails: null,
          upcomingToday: null,
          isExamToday: false,
          invalidRoom: true,
          invalidBuilding: false,
          roomsInBuilding,
          checkDate: selectedDate || 'today'
        });
        setIsSearching(false);
        return;
      }
      
      // Find ALL exams in this room (any date)
      const roomExams = examSchedule.filter(
        exam => exam.building === building && exam.room === room
      );
      
      // If checking for today
      if (!selectedDate) {
        // Find exams in this room SPECIFICALLY FOR TODAY using proper date comparison
        const todayExams = roomExams.filter(exam => {
          const examDate = parseExamDate(exam.date);
          return isSameDay(examDate, now);
        });
        
        // Check for ongoing exams
        const ongoingExams = todayExams.filter(exam => isExamOngoing(exam));
        
        const currentHour = now.getHours();
        const currentMinutes = now.getMinutes();
        
        // Filter to find only UPCOMING exams (later today)
        const upcomingExams = todayExams.filter(exam => {
          const examTime = exam.time.split(' - ')[0]; // Get start time
          const [timeStr, period] = examTime.split(' ');
          const [hourStr, minuteStr] = timeStr.split(':');
          let examHour = parseInt(hourStr);
          const examMinutes = parseInt(minuteStr);
          
          // Convert to 24-hour format for comparison
          if (period === 'PM' && examHour < 12) examHour += 12;
          if (period === 'AM' && examHour === 12) examHour = 0;
          
          // Exam is upcoming if it's later today
          return (examHour > currentHour) || 
                 (examHour === currentHour && examMinutes > currentMinutes);
        });
        
        // Find completed exams (already happened today)
        const completedExams = todayExams.filter(exam => {
          // Skip if it's ongoing
          if (isExamOngoing(exam)) return false;
          
          const examEndTime = exam.time.split(' - ')[1]; // Get end time
          const [timeStr, period] = examEndTime.split(' ');
          const [hourStr, minuteStr] = timeStr.split(':');
          let examHour = parseInt(hourStr);
          const examMinutes = parseInt(minuteStr);
          
          // Convert to 24-hour format for comparison
          if (period === 'PM' && examHour < 12) examHour += 12;
          if (period === 'AM' && examHour === 12) examHour = 0;
          
          // Exam is completed if end time is in the past
          return (examHour < currentHour) || 
                 (examHour === currentHour && examMinutes < currentMinutes);
        });
        
        // Update the search result for today
        onResultUpdate({
          available: ongoingExams.length === 0 && upcomingExams.length === 0, 
          examDetails: upcomingExams.length > 0 ? upcomingExams[0] : null,
          upcomingToday: upcomingExams.length > 0 ? upcomingExams[0] : null, // Keep for compatibility
          upcomingExams: upcomingExams.length > 0 ? upcomingExams : null, // Add this line
          hasUpcomingExams: upcomingExams.length > 0, // Add this line
          ongoingExam: ongoingExams.length > 0 ? ongoingExams[0] : null,
          hasOngoingExam: ongoingExams.length > 0,
          completedExams: completedExams.length > 0 ? completedExams : null,
          hasCompletedExamsOnly: completedExams.length > 0 && ongoingExams.length === 0 && upcomingExams.length === 0,
          invalidRoom: false,
          checkDate: 'today'
        });
      } 
      // If checking for a specific date
      else {
        // Find exams for the selected date
        const dateExams = roomExams.filter(exam => exam.date === selectedDate);
        
        // Update the search result for the specific date
        onResultUpdate({
          available: dateExams.length === 0,
          examDetails: dateExams.length > 0 ? dateExams[0] : null,
          dateExams: dateExams, // All exams on that date
          isExamOnDate: dateExams.length > 0,
          invalidRoom: false,
          checkDate: selectedDate
        });
      }
      
      setIsSearching(false);
    }, 800);
  };
  
  return { isSearching, checkAvailability };
};

export default RoomAvailability;